import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'

interface DeleteConfirmationProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  /**
   * A consequence of this delete that reaches further than the row, shown between the description and
   * the buttons. Optional on purpose: the twenty other dialogs built from this component have nothing
   * extra to say, and a required prop would have made this a twenty-file change.
   */
  notice?: ReactNode
  confirmLabel?: string
  isDeleting?: boolean
  onConfirm: () => void
}

/**
 * The last stop before something is destroyed, so every word in it has to be readable.
 *
 * The three texts are the caller's, because only the caller knows what is being deleted. The cancel
 * label is not: it is the same word in every one of the twenty dialogs built from this component, so
 * it is translated here rather than passed in twenty times — a prop would have left it untranslated
 * wherever a caller forgot it, which is exactly how it ended up reading "Cancel" next to a title in
 * Spanish.
 */
export function DeleteConfirmation({
  open,
  onOpenChange,
  title = 'Delete item',
  description = 'This action cannot be undone.',
  notice,
  confirmLabel = 'Delete',
  isDeleting = false,
  onConfirm,
}: DeleteConfirmationProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {notice !== undefined && notice !== null && (
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
            {notice}
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
